/**
 * Stabsverkehr: Fernschreiben, Telegramme, Wetterdienst. Knapper Meldestil ohne Artikel, wo der
 * Fernschreiber sie weglässt („Stellung bei Dornheim genommen“); Nationen und Orte mit der
 * richtigen Beugung aus grammar/de.ts.
 */
import type { UnitType } from '@friction/engine';
import type { dispatch as en } from '../en/dispatch';
import { adj, has, nation, theatre } from '../grammar/de';
import { type NationRef, placeDativeDe, placeLabel } from '../names';
import type { Translated } from '../types';

/** „varnische“ — oder „feindliche“, wenn die Nation unbekannt ist. */
const adjOr = (a: string | null, ending: string): string => (a ? adj(a, ending) : `feindlich${ending}`);
/** Nation mit Artikel im Fall — oder „der Feind“. */
const theNation = (n: NationRef | null, c: 'nom' | 'acc' | 'dat'): string =>
  n ? nation(n).the(c) : { nom: 'der Feind', acc: 'den Feind', dat: 'dem Feind' }[c];
const capFirst = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
const divisions = (n: number): string => (n === 1 ? 'Division' : 'Divisionen');

/** Was eine Meldung über einen gesichteten Feind nennt (Akkusativ nach „meldet“). */
const SIGHTED: Record<UnitType, string> = {
  infantry: 'feindliche Infanterie',
  motorized: 'feindliche mot. Infanterie',
  armor: 'feindliche Panzer',
  artillery: 'feindliche Artillerie',
  recon: 'feindliche Aufklärung',
  hq: 'ein feindliches Hauptquartier',
  decoy: 'einen feindlichen Verband',
};

/** „NEUAUFSTELLUNG — Panzerdivision in Dornheim“: der Verband, den die Stadt aufgestellt hat. */
const RAISED: Record<UnitType, string> = {
  infantry: 'Infanteriedivision',
  motorized: 'Infanteriedivision (mot.)',
  armor: 'Panzerdivision',
  artillery: 'Artilleriedivision',
  recon: 'Kavalleriedivision',
  hq: 'Armeeoberkommando',
  decoy: 'Scheinverband',
};

export const dispatch: Translated<typeof en> = {
  'tty.head.contact': 'FEINDBERÜHRUNG',
  'tty.head.assault': 'ANGRIFF',
  'tty.head.underAttack': 'FEINDANGRIFF',
  'tty.head.success': 'ERFOLG',
  'tty.head.repulsed': 'ABGEWIESEN',
  'tty.head.breakthrough': 'DURCHBRUCH',
  'tty.head.held': 'GEHALTEN',
  'tty.head.withdrawal': 'RÜCKZUG',
  'tty.head.pursuit': 'VERFOLGUNG',
  'tty.head.surrender': 'ÜBERGABE',
  'tty.head.attrition': 'AUFGERIEBEN',
  'tty.head.loss': 'VERLUST',
  'tty.head.prisoners': 'GEFANGENE',
  'tty.head.destroyed': 'VERNICHTET',
  'tty.head.captured': 'EINGENOMMEN',
  'tty.head.lost': 'VERLOREN',
  'tty.head.kessel': 'KESSEL',
  'tty.head.signal': 'MELDUNG',
  'tty.head.courierMissing': 'KURIER VERMISST',
  'tty.head.intercepted': 'ABGEFANGEN',
  'tty.head.decrypted': 'ENTZIFFERT',
  'tty.head.mobilised': 'NEUAUFSTELLUNG',
  'tty.head.research': 'FORSCHUNG',
  'tty.head.deception': 'TÄUSCHUNG',
  'tty.head.decoy': 'ATTRAPPE',
  'tty.head.hqLost': 'HQ VERLOREN',
  'tty.head.hqDestroyed': 'HQ VERNICHTET',
  'tty.head.capitulation': 'KAPITULATION',
  'tty.head.armistice': 'WAFFENSTILLSTAND',
  'tty.head.generalStaff': 'GENERALSTAB',
  'tty.head.met': 'WETTERDIENST',

  'tty.contact': '{unit} meldet {what} {at}',
  'tty.contact.ourPatrol': 'eigener Spähtrupp',
  'tty.what': ({ type }) => (type ? SIGHTED[type] : 'Feindkontakt'),
  'tty.assault': 'eigener Angriff {at} hat begonnen',
  'tty.underAttack': ({ adj: a, at }) => `${adjOr(a, 'e')} Truppen greifen unsere Stellungen ${at} an`,
  'tty.fighting': 'Kampfhandlungen {at} beobachtet',
  'tty.success': 'Stellung {at} genommen',
  'tty.repulsed': 'eigener Angriff {at} abgewiesen',
  'tty.breakthrough': 'unsere Linie {at} durchbrochen',
  'tty.held': 'Feindangriff {at} abgeschlagen',
  'tty.actionCarried': 'Gefecht {at} beendet — Angreifer hat die Stellung genommen',
  'tty.actionHeld': 'Gefecht {at} beendet — Verteidigung hat gehalten',
  'tty.withdrawal': ({ unit, from }) => `${unit} setzt sich von ${placeDativeDe(from)} ab`,
  'tty.pursuit': 'Feind weicht {at} zurück',
  'tty.surrender': '{unit} hat {at} die Waffen gestreckt',
  'tty.attrition': '{unit} aus Mangel an Versorgung aufgelöst',
  'tty.loss': '{unit} {at} vernichtet',
  'tty.enemyLoss': ({ unit, adj: a, cause, at }) => {
    const who = `${unit} (${a ? adj(a, '') : 'feindlich'})`;
    if (cause === 'surrender') return `${who} hat sich ${at} ergeben`;
    return `${who} ${at} ${cause === 'attrition' ? 'aufgerieben' : 'vernichtet'}`;
  },
  'tty.captured': ({ city, adj: a }) => `${city} genommen — ${adjOr(a, 'e')} Besatzung vertrieben`,
  'tty.lost': ({ city, adj: a }) => `${city} an ${adjOr(a, 'e')} Truppen gefallen`,
  'tty.fallsTo': ({ city, to }) => `${city} fällt an ${theNation(to, 'acc')}`,
  'tty.kesselOurs': ({ n, at }) => `${n} eigene ${divisions(n)} ${at} eingekesselt`,
  'tty.kesselTheirs': ({ n, adj: a, at }) => `${n} ${adjOr(a, 'e')} ${divisions(n)} ${at} eingekesselt`,
  'tty.acknowledged': '{unit} bestätigt Befehl ({h} Std. unterwegs)',
  'tty.executing': '{unit} führt Befehl aus',
  'tty.courierLost': 'Meldereiter zu {unit} {at} überfällig — Befehl womöglich in Feindeshand',
  'tty.intercept': ({ method, unit, order, hhour }) =>
    // The head says how (ABGEFANGEN / ENTZIFFERT); the line reads like the captured paper itself.
    `${method === 'captured-courier' ? 'Meldereiter gefangen, Befehl an' : 'Funkspruch an'} ${unit}: ${order}${hhour ? `, X-Zeit T${hhour.day} ${hhour.hour.replace(':', '')}` : ''}`,
  'tty.raised': ({ type, city }) => `${RAISED[type]} in ${city}`,
  'tty.researched': '{tech} eingeführt',
  'tty.decoyOurs': 'eigener Scheinverband {at} enttarnt',
  'tty.decoyTheirs': 'Kontakt {at} als Scheinverband enttarnt',
  'tty.hqLost': '{unit} überrannt — Befehle werden sich verzögern',
  'tty.hqDestroyed': ({ adj: a }) => `${adjOr(a, 'es')} Hauptquartier ausgeschaltet`,
  'tty.capitulatedUs': 'die Regierung hat kapituliert',
  'tty.capitulated': ({ nation: n, by }) =>
    `${capFirst(theNation(n, 'nom'))} ${n ? has(nation(n)) : 'hat'} vor ${theNation(by, 'dat')} kapituliert`,
  'tty.armistice': 'die Kampfhandlungen sind eingestellt',
  'tty.war': ({ theatre: name }) =>
    `Feindseligkeiten ${theatre(name).inside} eröffnet. Alle Verbände in Alarmbereitschaft`,
  'tty.forecast': ({ what, region, when, chance }) =>
    `${what} über ${placeDativeDe(region)} ${when} erwartet (${chance} %)`,
  'tty.forecast.dawn': 'im Morgengrauen',
  'tty.forecast.by': 'bis {clock}',

  // ---- Befehle im Nominalstil der Befehlssprache
  'orderPhrase.move': ({ place }) => `Marsch in den Raum ${placeLabel(place, 'de')}`,
  'orderPhrase.attack': ({ place }) =>
    place.kind === 'city' || place.kind === 'near'
      ? `Angriff auf ${placeLabel(place, 'de')}`
      : `Angriff im Raum ${placeLabel(place, 'de')}`,
  'orderPhrase.hold': ({ place }) => `Halten des Raumes ${placeLabel(place, 'de')}`,
  'orderPhrase.retreat': ({ place }) => `Ausweichen in den Raum ${placeLabel(place, 'de')}`,
  'orderPhrase.posture': ({ posture }) =>
    posture === 'aggressive'
      ? 'Übergang zu offensiver Haltung'
      : posture === 'defensive'
        ? 'Übergang zu defensiver Haltung'
        : posture === 'balanced'
          ? 'Übergang zu ausgewogener Haltung'
          : 'neue Haltung',

  // ---- Wetter
  'wx.cond.rain': 'Regen',
  'wx.cond.storm': 'Schwere Unwetter',
  'wx.cond.snow': 'Schneefall',
  'wx.cond.blizzard': 'Schneestürme',
  'wx.cond.fog': 'Dichter Morgennebel',
  'wx.change.frost.head': 'STRENGER FROST',
  'wx.change.frost': 'Boden im ganzen Kriegsgebiet gefroren — Flüsse und Sümpfe tragen, der Schlamm ist fort',
  'wx.change.thaw.head': 'TAUWETTER',
  'wx.change.thaw': 'Eisgang auf den Flüssen — die Wege werden weich',
  'wx.change.mud.head': 'RASPUTIZA',
  'wx.change.mud': 'die Felder sind grundlos — Panzer und Lastwagen an die Straßen gebunden',
  'wx.change.firm.head': 'BODEN FEST',
  'wx.change.firm': 'die Felder trocknen ab — Fahrzeuge können die Straßen wieder verlassen',
  'wx.change.snow.head': 'SCHNEE',
  'wx.change.snow': 'Schnee liegt über dem Kriegsgebiet — abseits der Straßen schweres Vorankommen',
  'wx.fx.marching': 'Marsch {pct}',
  'wx.fx.attacks': 'Angriffe {pct}',
  'wx.fx.sight': 'Sicht −{n}',
  'wx.fx.hidden': 'Verbände ab 1 Feld Abstand verborgen',
  'wx.fx.attackers': 'Angreifer {pct}',
  'wx.fx.noAir': 'keine Luftbilder',
  'wx.fx.mud': 'abseits der Straßen: zu Fuß {foot}, Fahrzeuge {motor}',
  'wx.fx.tankAttacks': 'Panzerangriffe {pct}',
  'wx.fx.snow': 'abseits der Straßen {pct}',
  'wx.fx.frozenMarsh': 'gefrorener Sumpf trägt wie Ebene',
  'wx.fx.frozenRivers': 'Flüsse gefroren: keine Brücke nötig',

  // ---- Telegramme
  'tg.cityCaptured.title': 'Stadt genommen',
  'tg.cityCaptured': ({ city, adj: a, size }) =>
    `${city} ist in unserer Hand. Die ${adjOr(a, 'e')} Besatzung ist aus der ${['', 'Kleinstadt', 'Stadt', 'Großstadt'][size]} vertrieben.`,
  'tg.cityCaptured.stamp': 'Genommen',
  'tg.cityLost.title': 'Stadt verloren',
  'tg.cityLost': ({ city, adj: a }) =>
    `${city} ist an ${adjOr(a, 'e')} Truppen gefallen. Ihre Industrie ist für uns verloren.`,
  'tg.cityLost.stamp': 'Verloren',
  'tg.kesselOurs.title': 'Einkesselung',
  'tg.kesselOurs': {
    one: 'Eine unserer Divisionen ist {at} abgeschnitten. Die Versorgung ist abgerissen. Ausbrechen oder entsetzen.',
    other:
      '{n} unserer Divisionen sind {at} abgeschnitten. Die Versorgung ist abgerissen. Ausbrechen oder entsetzen.',
  },
  'tg.kesselTheirs.title': 'Kessel geschlossen',
  'tg.kesselTheirs': ({ n, adj: a, at }) =>
    n === 1
      ? `Eine ${adjOr(a, 'e')} Division ist ${at} eingeschlossen. Den Ring halten, und sie verhungert.`
      : `${n} ${adjOr(a, 'e')} Divisionen sind ${at} eingeschlossen. Den Ring halten, und sie verhungern.`,
  'tg.kessel.stamp': 'Kessel',
  'tg.courierCaptured.title': 'Kurier gefangen',
  'tg.radioDecrypt.title': 'Funkspruch entziffert',
  'tg.intercept': ({ unit, order, hhour }) =>
    `Befehl an ${unit}: ${order}.${hhour ? ` X-Zeit: Tag ${hhour.day}, ${hhour.hour} Uhr.` : ''}`,
  'tg.intercepted.stamp': 'Abgefangen',
  'tg.decrypted.stamp': 'Entziffert',
  'tg.courierLost.title': 'Kurier vermisst',
  'tg.courierLost':
    'Der Meldereiter mit Befehlen an {unit} ist nie angekommen. Es ist anzunehmen, dass der Feind sie gelesen hat.',
  'tg.courierLost.stamp': 'Kompromittiert',
  'tg.research.title': 'Forschung abgeschlossen',
  'tg.research': '{name}: {desc}',
  'tg.research.stamp': 'Genehmigt',
  'tg.hqLost.title': 'Hauptquartier verloren',
  'tg.hqLost': ({ unit }) =>
    `${unit ?? 'Ein Hauptquartier'} ist überrannt worden. Befehle in seinen Abschnitt werden sich verzögern.`,
  'tg.hqLost.stamp': 'HQ verloren',
  'tg.war.title': 'Mobilmachung',
  'tg.war': ({ theatre: name }) =>
    `${capFirst(theatre(name).inside)} ist der Krieg ausgebrochen. Befehle verlassen die Hauptquartiere per Fernsprecher und Kurier — die Kuriere schützen, die Hauptquartiere nahe der Front halten und auf die Flanken achten.`,
  'tg.war.stamp': 'Befohlen',
  'tg.mud.title': 'Schlammzeit',
  'tg.mud':
    'Der Regen hat das Land aufgeweicht. Abseits der Straßen kriechen Panzer und Lastwagen mit halber Fahrt und greifen schlecht an; die Nachschubkolonnen bleiben stecken. Die Straßen halten.',
  'tg.frost.title': 'Der Frost ist da',
  'tg.frost':
    'Der Boden ist hart gefroren. Flüsse und Sümpfe tragen Mann und Fahrzeug; der Schlamm ist fort. Der Winterkrieg beginnt.',
  'tg.thaw.title': 'Tauwetter',
  'tg.thaw':
    'Der Frost bricht. Auf den Flüssen geht das Eis, der Boden wird weich — abseits der Straßen ist mit Schlamm zu rechnen.',
  'tg.met.stamp': 'Wetterdienst',
  'tg.capitulation.title': 'Kapitulation',
  'tg.capitulationUs':
    'Die Regierung hat die Bedingungen angenommen. Unsere Truppen haben Befehl, die Waffen niederzulegen.',
  'tg.capitulation': ({ nation: n, by }) =>
    `${capFirst(theNation(n, 'nom'))} ${n ? has(nation(n)) : 'hat'} vor ${theNation(by, 'dat')} kapituliert. Das Gebiet fällt an die Besatzer.`,
  'tg.capitulation.stamp': 'Kapituliert',
};
